import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageShell, btnDanger, btnGhost, btnPrimary, cellCls, headCls, inputCls } from "@/components/TopBar";

const EMPTY = { name: "", assigned_vehicle_id: "" };

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [supervisor, setSupervisor] = useState({ supervisor_name: "", supervisor_phone: "" });

  const load = useCallback(async () => {
    const [d, v, s] = await Promise.all([api.get("/drivers"), api.get("/vehicles"), api.get("/settings")]);
    setDrivers(d.data);
    setVehicles(v.data);
    setSupervisor({
      supervisor_name: s.data.supervisor_name || "",
      supervisor_phone: s.data.supervisor_phone || "",
    });
  }, []);

  const saveSupervisor = async () => {
    try {
      await api.patch("/settings", supervisor);
      toast.success("Client-facing contact saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load drivers"));
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Driver name is required");
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.assigned_vehicle_id) delete body.assigned_vehicle_id;
      await api.post("/drivers", body);
      toast.success("Driver added");
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/drivers/${id}`, body);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete driver ${d.name}?`)) return;
    try {
      await api.delete(`/drivers/${d.id}`);
      toast.success("Driver deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <PageShell
      testid="drivers-page"
      subtitle="administration"
      title="DRIVER MANAGEMENT"
      actions={
        <button data-testid="new-driver-btn" onClick={() => setShowForm((s) => !s)} className={btnPrimary}>
          <Plus size={12} /> {showForm ? "CLOSE" : "ADD DRIVER"}
        </button>
      }
    >
      <div
        className="mb-8 grid gap-4 border border-white/15 bg-[#121212] p-5 sm:grid-cols-3"
        data-testid="supervisor-settings"
      >
        <div>
          <label className="overline mb-2 block">supervisor name (shown to clients)</label>
          <input
            data-testid="supervisor-name-input"
            value={supervisor.supervisor_name}
            onChange={(e) => setSupervisor({ ...supervisor, supervisor_name: e.target.value })}
            className={inputCls}
          />
        </div>
        <div>
          <label className="overline mb-2 block">supervisor phone</label>
          <input
            data-testid="supervisor-phone-input"
            value={supervisor.supervisor_phone}
            onChange={(e) => setSupervisor({ ...supervisor, supervisor_phone: e.target.value })}
            placeholder="+91 90000 00001"
            className={inputCls}
          />
        </div>
        <div className="flex flex-col justify-end gap-2">
          <button data-testid="save-supervisor-btn" onClick={saveSupervisor} className={btnGhost}>
            SAVE CLIENT CONTACT
          </button>
          <p className="overline">
            clients only ever see this number - driver records hold a name only
          </p>
        </div>
      </div>

      {showForm && (
        <form
          onSubmit={create}
          data-testid="driver-form"
          className="mb-8 grid gap-4 border border-white/15 bg-[#121212] p-5 sm:grid-cols-3"
        >
          <div>
            <label className="overline mb-2 block">driver name</label>
            <input
              data-testid="driver-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">assign truck</label>
            <select
              data-testid="driver-vehicle-input"
              value={form.assigned_vehicle_id}
              onChange={(e) => setForm({ ...form, assigned_vehicle_id: e.target.value })}
              className={inputCls}
            >
              <option value="">- unassigned -</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate_number}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={busy} data-testid="submit-driver-btn" className={`${btnPrimary} w-full py-2.5`}>
              {busy ? "saving" : "save driver"}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[680px]" data-testid="drivers-table">
          <thead className="border-b border-white/10 bg-[#121212]">
            <tr>
              <th className={headCls}>driver name</th>
              <th className={headCls}>assigned truck</th>
              <th className={headCls}>visible to client</th>
              <th className={headCls}>active</th>
              <th className={headCls}>actions</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map((d) => (
              <tr key={d.id} data-testid={`driver-row-${d.id}`} className="border-b border-white/5 hover:bg-[#141414]">
                <td className={`${cellCls} font-head font-bold`}>{d.name}</td>
                <td className={cellCls}>
                  <select
                    data-testid={`driver-assign-select-${d.id}`}
                    value={d.assigned_vehicle_id || ""}
                    onChange={(e) => patch(d.id, { assigned_vehicle_id: e.target.value || null })}
                    className={inputCls}
                  >
                    <option value="">- unassigned -</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.plate_number}
                      </option>
                    ))}
                  </select>
                </td>
                <td className={cellCls}>
                  <button
                    data-testid={`toggle-client-visible-btn-${d.id}`}
                    onClick={() => patch(d.id, { show_to_client: !(d.show_to_client !== false) })}
                    className={btnGhost}
                    style={d.show_to_client !== false ? { borderColor: "#00FF6655", color: "#00FF66" } : {}}
                  >
                    {d.show_to_client !== false ? "SHOWN" : "HIDDEN"}
                  </button>
                </td>
                <td className={cellCls} style={{ color: d.is_active ? "#00FF66" : "#FF3B30" }}>
                  {d.is_active ? "YES" : "NO"}
                </td>
                <td className={cellCls}>
                  <div className="flex gap-2">
                    <button
                      data-testid={`toggle-driver-btn-${d.id}`}
                      onClick={() => patch(d.id, { is_active: !d.is_active })}
                      className={btnGhost}
                    >
                      {d.is_active ? "DEACTIVATE" : "ACTIVATE"}
                    </button>
                    <button
                      data-testid={`delete-driver-btn-${d.id}`}
                      onClick={() => remove(d)}
                      className={btnDanger}
                      aria-label="Delete driver"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}
